import { PaymentType } from '@/types/common';

/**
 * 校验工具类
 */

// 手机号正则表达式（支持最新号段）
export const PHONE_REGEX = /^1[3-9]\d{9}$/;

// 邮箱正则表达式
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// 金额的正则表达式(最多两位小数)
export const MONEY_REGEX = /^\d+(\.\d{1,2})?$/;

// 大于0的正整数正则表达式
export const POSITIVE_INTEGER_REGEX = /^[1-9]\d*$/;

// 数字正则表达式
export const NUMBER_REGEX = /^\d+$/;

// 最多输入一位小数的正则表达式
export const MAX_ONE_DECIMAL_REGEX = /^\d+(\.\d{0,1})?$/;

/**
 * 校验手机号
 * @param phone 手机号
 * @returns boolean
 */
export const isValidPhone = (phone: string): boolean => {
  if (!phone) return false;
  return PHONE_REGEX.test(phone);
};

/**
 * 校验邮箱
 * @param email 邮箱地址
 * @returns boolean
 */
export const isValidEmail = (email: string): boolean => {
  if (!email) return false;
  return EMAIL_REGEX.test(email);
};

/**
 * 格式化手机号 (添加空格: 133 1234 5678)
 * @param phone 手机号
 * @returns string
 */
export const formatPhone = (phone: string): string => {
  if (!phone) return '';
  const value = phone.replace(/\s/g, '');
  const matches = value.match(/^(\d{3})(\d{4})(\d{4})$/);
  if (matches) {
    return `${matches[1]} ${matches[2]} ${matches[3]}`;
  }
  return phone;
};

/**
 * 隐藏手机号中间四位
 * @param phone 手机号
 * @returns string
 */
export const hidePhone = (phone: string): string => {
  if (!phone) return '';
  return phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2');
};

/**
 * 隐藏邮箱中间部分
 * @param email 邮箱地址
 * @returns string
 */
export const hideEmail = (email: string): string => {
  if (!email) return '';
  return email.replace(/^(.{3}).*(@.*)$/, '$1***$2');
};

/**
 * 判断百分比是否为正数
 * @param value 百分比字符串
 * @returns boolean
 */
export function isPositivePercent(value: string): boolean {
  if (!value) {
    return false;
  }
  const num = parseFloat(value.replace('%', ''));
  if (isNaN(num)) {
    return false;
  }
  return num > 0;
}

/**
 * 统一校验方法
 */
export const validate = {
  /**
   * 校验手机号
   * @param phone 手机号
   * @param showError 是否显示错误提示
   * @returns boolean
   */
  phone: (phone: string, showError = false): boolean => {
    const valid = isValidPhone(phone);
    if (!valid && showError) {
      // 如果需要显示错误提示，可以在这里集成你的提示组件
      console.error('请输入正确的手机号');
    }
    return valid;
  },

  /**
   * 校验邮箱
   * @param email 邮箱地址
   * @param showError 是否显示错误提示
   * @returns boolean
   */
  email: (email: string, showError = false): boolean => {
    const valid = isValidEmail(email);
    if (!valid && showError) {
      // 如果需要显示错误提示，可以在这里集成你的提示组件
      console.error('请输入正确的邮箱地址');
    }
    return valid;
  },
};

/**
 * 校验支付价格/次数
 * @param value 输入值
 * @param paymentType 支付类型 (PaymentType)
 * @param label 字段名称（用于错误提示，默认为"价格"）
 * @returns Promise<void>
 */
export const validatePaymentPrice = (
  value: any,
  paymentType: PaymentType,
  label: string = '价格',
): Promise<void> => {
  // 必填校验
  if (!value && value !== 0) {
    // 期限卡如果不输入，视为不扣减天数（0天），所以允许为空（业务上通常代表0）
    // 但如果业务明确要求必须填0才能代表0，则这里需要保持必填。
    // 根据需求描述“如果是期限卡，输入框可以不输入内容”，则跳过必填校验
    if (paymentType === PaymentType.DAY) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(`请输入${label}`));
  }

  const numValue = Number(value);

  // 格式校验（虽然 Input 组件限制了输入，但作为兜底校验）
  if (paymentType === PaymentType.TIMES) {
    // 次卡：大于0且最多一位小数
    if (numValue <= 0) {
      return Promise.reject(new Error('请输入大于0的数值'));
    }
    if (!MAX_ONE_DECIMAL_REGEX.test(String(value))) {
      return Promise.reject(new Error('次卡最多输入一位小数'));
    }
  } else if (paymentType === PaymentType.DAY) {
    // 期限卡：整数
    if (!Number.isInteger(numValue)) {
      return Promise.reject(new Error('期限卡只能输入整数'));
    }
  }

  return Promise.resolve();
};

export default validate;
